import { z } from "zod";

import { CLAUSE_CHANGE_KINDS } from "./contractComparison";

/**
 * AI REVİZYON ÖNERİSİ ve FARK AÇIKLAMASI (CLOS-207 `[190]` · CLOS-1005).
 *
 * ### ⚠ İKİ AYRI YETENEK, TEK KART
 *
 * 1. **Revizyon önerisi**: belirli bir değişmez versiyonun belirli bir maddesi için
 *    ÖNERİLEN metin. Kanonik revizyon kaydı DEĞİL — kullanıcı açıkça kaydedene kadar
 *    CLOS-138 hiç çalışmıyor.
 * 2. **Fark açıklaması**: iki değişmez versiyon arasındaki DETERMİNİSTİK farkı (CLOS-140)
 *    düz Türkçeyle anlatan katman. Farkı AI ÜRETMİYOR; üretilmiş farkı açıklıyor.
 *
 * ### ⚠ AÇIKLAMA FARKIN YERİNE GEÇMİYOR — ÇELİŞKİ DETERMİNİSTİK ÖLÇÜLÜYOR
 *
 * Kart: *"AI açıklaması ... gerçek redline ile çelişmeyecek şekilde kaynaklandırır;
 * açıklama ile deterministik fark açıkça ayrılır."* Bu yüzden açıklamanın her satırı bir
 * `clauseId` ve bir `change` sınıfı taşıyor ve `farkAciklamasiCeliskileri` bunları GERÇEK
 * fark sonucuyla karşılaştırıyor. Modelin "şu madde silindi" dediği yerde fark "değişti"
 * diyorsa, bu bir çelişki olarak İŞARETLENİYOR — ekran o satırı uyarıyla gösteriyor ve
 * karar kaynağı olarak deterministik farkı bırakıyor.
 */

/**
 * ÖNERİ ÇIKTISI.
 *
 * ⚠ `assumptions` ZORUNLU BİR ALAN (boş dizi olabilir): kart *"kaynakta olmayan ticari
 * veya hukuki bilgiyi kesin hüküm gibi eklemez; kullanıcıdan gerekli bağlam istenir veya
 * açık placeholder kullanılır"* diyor. Model bir rakam/tarih uydurmak yerine varsayımını
 * yazmak zorunda; alan olmasaydı uydurmanın maliyeti sıfır olurdu.
 *
 * ⚠ `purpose` MODELİN ANLADIĞI AMAÇ, kullanıcının yazdığı amaç değil: ikisi ayrışıyorsa
 * (kullanıcı "süreyi kısalt" dedi, model "cezayı artırdım" anladı) kullanıcı bunu ancak
 * modelin kendi cümlesini görerek fark eder.
 */
export const contractAiRevisionSuggestionSchema = z.strictObject({
  proposedText: z.string().min(1).max(20_000),
  rationale: z.string().min(1).max(2000),
  purpose: z.string().max(500).default(""),
  assumptions: z.array(z.string().max(500)).max(20).default([]),
  /** ⚠ Kullanıcıdan istenen bağlam: model uydurmak yerine SORUYOR. */
  missingContext: z.array(z.string().max(300)).max(20).default([]),
  confidence: z.enum(["high", "medium", "low"]),
});
export type ContractAiRevisionSuggestion = z.infer<typeof contractAiRevisionSuggestionSchema>;

/**
 * FARK AÇIKLAMASININ TEK SATIRI.
 *
 * ⚠ `clauseId` ve `change` ZORUNLU: açıklamayı deterministik farka BAĞLAYAN iki alan
 * bunlar. Serbest metin bir paragraf olsaydı, açıklamanın hangi maddeye ait olduğu ve
 * farkla çelişip çelişmediği ölçülemezdi — kartın en kritik kriteri ölçülemez hâle gelirdi.
 */
export const contractAiCompareNoteSchema = z.strictObject({
  clauseId: z.string().min(1).max(200),
  change: z.enum(CLAUSE_CHANGE_KINDS),
  explanation: z.string().min(1).max(1000),
});
export type ContractAiCompareNote = z.infer<typeof contractAiCompareNoteSchema>;

export const contractAiCompareExplanationSchema = z.strictObject({
  /** Farkın tamamının bir cümlelik özeti — hangi maddelerin değiştiğini saymaz, ANLATIR. */
  summary: z.string().max(2000).default(""),
  notes: z.array(contractAiCompareNoteSchema).max(200).default([]),
});
export type ContractAiCompareExplanation = z.infer<typeof contractAiCompareExplanationSchema>;

export const CONTRACT_AI_REVISION_KINDS = ["suggestion", "compare_explanation"] as const;
export type ContractAiRevisionKind = (typeof CONTRACT_AI_REVISION_KINDS)[number];

export const CONTRACT_AI_REVISION_ERROR_CODES = ["provider_error", "invalid_output"] as const;
export type ContractAiRevisionErrorCode = (typeof CONTRACT_AI_REVISION_ERROR_CODES)[number];

export const CONTRACT_AI_REVISION_ERROR_MESSAGES: Record<ContractAiRevisionErrorCode, string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; sonuç üretilemedi ve krediniz iade edildi.",
  invalid_output: "AI beklenen biçimde bir sonuç üretmedi; kaydedilmedi ve krediniz iade edildi.",
};

/**
 * ⚠ EKRANDA ZORUNLU: açıklama karar kaynağı değil. Kart: *"Karşılaştırma açıklaması onay,
 * mutabakat, imza uygunluğu veya hukuki etki kararı üretmez."*
 */
export const CONTRACT_AI_COMPARE_ADVISORY =
  "Bu açıklama, sistemin hesapladığı farkı okumayı kolaylaştırmak içindir; farkın kendisi " +
  "değildir. Çelişki hâlinde deterministik fark esastır.";

export const CONTRACT_AI_REVISION_CONTRADICTION_WARNING =
  "AI açıklaması sistemin hesapladığı farkla örtüşmüyor; bu satırda deterministik fark esastır.";

/**
 * ÇELİŞKİ TESPİTİ — DETERMİNİSTİK, MODELE SORULMUYOR.
 *
 * ⚠ Modelin "çelişki var mı" sorusuna kendi cevabını yazmasını isteyebilirdik; o cevap da
 * modelin bir iddiası olurdu ve tam da güvenmediğimiz şeyi güvenilir sanardık. Burada
 * karşılaştırma KOD ile yapılıyor: açıklamanın işaret ettiği madde gerçek farkta var mı,
 * ve orada bildirilen değişim sınıfı AYNI mı.
 *
 * ⚠ Dönen değer AÇIKLAMA SATIRLARININ İNDEKSLERİ: ekran hangi satırın çeliştiğini
 * göstermek zorunda. "Çelişki var/yok" gibi tek bir boolean, on satırlık bir açıklamada
 * kullanıcıya hangisine güvenmeyeceğini söylemezdi.
 */
export function farkAciklamasiCeliskileri(
  aciklama: ContractAiCompareExplanation,
  fark: { clauses: { clauseId: string; change: string }[] },
): number[] {
  const gercek = new Map(fark.clauses.map((m) => [m.clauseId, m.change]));
  const celisenler: number[] = [];
  aciklama.notes.forEach((not, i) => {
    const gercekDegisim = gercek.get(not.clauseId);
    /** Farkta hiç olmayan bir madde ya da farklı bir değişim sınıfı: ikisi de çelişki. */
    if (gercekDegisim === undefined || gercekDegisim !== not.change) celisenler.push(i);
  });
  return celisenler;
}

export type ContractAiRevisionRunSummary = {
  id: string;
  contractId: string;
  kind: ContractAiRevisionKind;
  status: "completed" | "failed";
  /** Öneri: kaynak versiyon; açıklama: `null`. */
  sourceVersionId: string | null;
  targetClauseId: string | null;
  targetBlockIndex: number | null;
  currentText: string | null;
  /** Açıklama: karşılaştırılan iki versiyon; öneri: `null`. */
  leftVersionId: string | null;
  rightVersionId: string | null;
  suggestion: ContractAiRevisionSuggestion | null;
  explanation: ContractAiCompareExplanation | null;
  /** ⚠ Çelişen açıklama satırlarının indeksleri — üretim anında hesaplanıp saklanıyor. */
  contradictions: number[];
  /**
   * ⚠ Mevcut metinde geçip önerilen metinde GÖRÜNMEYEN sayı/oran belirteçleri (CLOS-1085).
   * Bir RED değil, bir uyarı: kullanıcı zaten tutarı değiştirmek istemiş olabilir.
   */
  droppedFacts: string[];
  /** ⚠ CLOS-206 bulgusu girdi olarak seçildiyse kimliği — bağ korunuyor (CLOS-1085). */
  riskFindingId: string | null;
  /** ⚠ Fark sonucu değiştiyse (yeni versiyon, farklı içerik) açıklama BAYAT. */
  stale: boolean;
  errorCode: ContractAiRevisionErrorCode | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  /** ⚠ Kanonik revizyona dönüştüyse kimliği — ikinci kaydetme aynı kaydı döndürüyor. */
  savedRevisionId: string | null;
  createdByName: string;
  createdAt: string;
};

/**
 * ÖNERİ İSTEĞİ.
 *
 * ⚠ `currentText` İSTEMCİDEN GELİYOR ama sunucu onu kaynak versiyondaki GERÇEK metinle
 * karşılaştırıyor (`createContractRevision`'ın aynı kuralı): kullanıcının ekranında
 * gördüğü metin ile üzerine öneri üretilen metin aynı olmalı. Göndermeseydik, model
 * kullanıcının hiç görmediği bir metne öneri yazabilirdi.
 */
export const createContractAiRevisionSuggestionSchema = z.strictObject({
  sourceVersionId: z.uuid("Geçerli bir kaynak versiyon seçilmelidir"),
  targetClauseId: z.uuid("Geçerli bir madde seçilmelidir"),
  targetBlockIndex: z.coerce.number().int().min(0).nullish(),
  currentText: z.string().max(20_000),
  /** Kullanıcının ne istediği — serbest metin, allowlist'te tanımlı tek bağlam alanı. */
  instruction: z.string().min(1, "Ne istediğinizi yazın").max(2000),
  /** ⚠ İSTEĞE BAĞLI CLOS-206 girdisi: bulgu referansı taşınır, revizyon otomatik üretilmez. */
  riskFindingId: z.uuid().nullish(),
});
export type CreateContractAiRevisionSuggestionInput = z.infer<
  typeof createContractAiRevisionSuggestionSchema
>;

export const createContractAiCompareExplanationSchema = z.strictObject({
  leftVersionId: z.uuid("Geçerli bir versiyon seçilmelidir"),
  rightVersionId: z.uuid("Geçerli bir versiyon seçilmelidir"),
});
export type CreateContractAiCompareExplanationInput = z.infer<
  typeof createContractAiCompareExplanationSchema
>;

/**
 * SESSİZ OLGU KAYBI TESPİTİ (CLOS-1085 kalite matrisi).
 *
 * ### ⚠ NE ÖLÇÜYOR: ANLAM DEĞİL, OLGU
 *
 * Kart *"kritik taraf/tutar/tarih/istisna/koşul kaybı olmamalı"* diyor. "Anlam korunuyor
 * mu" sorusunu bir modele sormak, güvenmediğimiz şeyi hakem yapmak olurdu. Bunun yerine
 * DETERMİNİSTİK olarak ölçülebilen dar bir alt küme alınıyor: mevcut metinde geçen SAYI,
 * ORAN ve TARİH benzeri belirteçlerin önerilen metinde de geçip geçmediği.
 *
 * ### ⚠ SONUÇ BİR RED DEĞİL, BİR UYARI
 *
 * Kullanıcı "bedeli 10.000 TL'den 15.000 TL'ye çıkar" dediyse eski tutarın kaybolması
 * İSTENEN şeydir. Bu yüzden fonksiyon "hata" demiyor, yalnız "şu belirteçler önerilen
 * metinde yok" diyor ve kararı ekrana/kullanıcıya bırakıyor. Reddetseydik, en meşru
 * revizyon isteğini imkânsız kılardık.
 */
const OLGU_KALIBI = /\d[\d.,]*\s*%?|%\s*\d[\d.,]*/g;

export function oneriKayipOlgular(mevcutMetin: string, onerilenMetin: string): string[] {
  const normalize = (m: string) => m.replace(/\s+/gu, "");
  const onerilen = normalize(onerilenMetin);
  const gorulen = new Set<string>();
  const kayip: string[] = [];

  for (const eslesme of mevcutMetin.match(OLGU_KALIBI) ?? []) {
    const belirteç = normalize(eslesme);
    /** ⚠ Tek haneli sayılar elenmiyor ama tekrarlar bir kez sayılıyor. */
    if (belirteç.length === 0 || gorulen.has(belirteç)) continue;
    gorulen.add(belirteç);
    if (!onerilen.includes(belirteç)) kayip.push(eslesme.trim());
  }
  return kayip;
}

export const CONTRACT_AI_REVISION_FACT_LOSS_WARNING =
  "Mevcut metindeki bazı sayı/oran/tarih bilgileri önerilen metinde görünmüyor. İstediğiniz " +
  "değişiklik buysa sorun yok; değilse öneriyi gözden geçirin.";
